import React, { useRef, useState, useEffect } from "react";
import { BehaviorSubject } from "rxjs";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Article from "../../models/Article";
import FormattedText from "../FormattedText";
import PublicationAuthors from "../PublicationAuthors";
import SourceEntity from "../../models/SourceEntity";
import LikeButton from "../SocialFeatures/LikeButton";
import threadService from "../../../../services/threadService";
import CommentButton from "../SocialFeatures/CommentButton";
import ContentButton from "../SocialFeatures/ContentButton";
import ClassificationButton from "../SocialFeatures/ClassificationButton";
import PubmedFormWidget from "../../../PubmedUserEntries/PubmedFormWidget";
import LinkVariantButton from "../SocialFeatures/LinkVariantButton";
import { pluralize } from "../../../../common/helpers/helpers";
import DownloadPdf from "../SocialFeatures/downloadPDF";
import Thread from "../../../social/Thread";
import HelperText from "../../../atoms/HelperText";
import TagList from "../TagList";
import PubmedEntry from "../../../PubmedUserEntries/PubmedEntry";
import Contributions from "../../../social/Contributions";
import isPortal from "../../../../common/helpers/isPortal";
import { getSearchEntity } from "../../../../state/selectors/Publications.selectors";

function PubmedArticle({ article, initTab, sourceEntity, searchEntity }) {
  const [tab, setTab] = useState(initTab || "abstract");
  const slug = sourceEntity?.getReactionSlugForPub(article.pubmedId);
  const highlighted$ = useRef(new BehaviorSubject(new Set())).current;
  const selected$ = useRef(new BehaviorSubject(new Set())).current;
  const variant = sourceEntity?.isVariant ? sourceEntity.id : null;
  const variants = variant ? [{ variant_id: variant }] : [];
  const isSameWithSearch = searchEntity?.id === sourceEntity?.id;
  const shouldShowClassifications = isSameWithSearch && sourceEntity?.isVariant;
  const isClinical = isPortal();

  const linkedBy = sourceEntity
    ? sourceEntity.getSourcesForArticle(article)
    : [];

  if (window.debug) {
    console.log(searchEntity, sourceEntity);
  }

  useEffect(() => {
    const { type, id } = sourceEntity || {};
    if (!type || !id) return;
    threadService
      .loadTopicAndComments(type, id, "publications", article.pubmedId)
      .subscribe({ error: console.error });
  }, [article, sourceEntity]);

  return article ? (
    <div className="pub-expanded font-roboto">
      <div className="tw-text-xl">
        {article.title.text ? (
          <FormattedText
            highlighted$={highlighted$}
            selected$={selected$}
            domTree={article.title.domTree}
          />
        ) : (
          <div className="tw-text-sm tw-text-blueGray-500">
            Title not available
          </div>
        )}
      </div>

      <div className="tw-h-1" />

      <div className="tw-text-sm">
        <PublicationAuthors article={article} />

        <div className="">
          {article.readableDate}
          {article.journalLong ? (
            <>
              <span className="tw-px-1">in</span>
              <span className="tw-min-w-0">{article.journalLong}</span>
            </>
          ) : null}
        </div>

        {linkedBy?.length ? <div>Linked by: {linkedBy.join(", ")}</div> : null}

        <div className="tw-flex tw-items-center">
          {article.citationCount === null ? null : (
            <>
              <div className="tw-flex-shrink-0">
                <span className="tw-font-bold">{article.citationCount} </span>
                {pluralize(article.citationCount, "citation", "citations")}
              </div>
              <div className="tw-mx-2 tw-flex-shrink-0 tw-w-1 tw-h-1 tw-bg-blueGray-400 tw-rounded-full" />
            </>
          )}

          <div className="tw-opacity-80 tw-text-xs tw-font-bold">PubMed: </div>
          <div className="tw-w-1" />
          <a
            target="_blank"
            href={article.pubmedLink}
            className="tw-inline-block"
          >
            {article.pubmedId}
          </a>
          <div className="tw-mx-2 tw-flex-shrink-0 tw-w-1 tw-h-1 tw-bg-blueGray-400 tw-rounded-full" />
          {article.doi ? (
            <>
              <div className="tw-opacity-80 tw-text-xs tw-font-bold">DOI: </div>
              <div className="tw-w-1" />
              <a
                target="_blank"
                href={`https://doi.org/${article.doi}`}
                className="tw-inline-block"
              >
                {article.doi}
              </a>
              <div className="tw-mx-2 tw-flex-shrink-0 tw-w-1 tw-h-1 tw-bg-blueGray-400 tw-rounded-full" />
            </>
          ) : null}

          <div className="tw-text-xs tw-inline">
            <DownloadPdf article={article} />
          </div>
        </div>
      </div>
      <div className="tw-h-2" />

      {sourceEntity?.id && !isClinical ? (
        <div className="tw-relative">
          <div className="tw-border-t tw-border-b tw-pr-32 tw-flex-wrap tw-my-3 tw-py-2 tw-flex tw-gap-x-5 tw-items-center tw-text-xs">
            <ContentButton
              sourceEntity={sourceEntity}
              article={article}
              isActive={tab === "abstract"}
              onSelect={() => setTab("abstract")}
            />

            {shouldShowClassifications ? (
              <ClassificationButton
                sourceEntity={sourceEntity}
                article={article}
                isActive={tab === "classifications"}
                onSelect={() => setTab("classifications")}
                slug={slug}
              />
            ) : null}

            {slug ? (
              <CommentButton
                isTab={true}
                sourceEntity={sourceEntity}
                article={article}
                isActive={tab === "comments"}
                onSelect={() => setTab("comments")}
                slug={slug}
              />
            ) : null}

            <LinkVariantButton
              isActive={tab === "link"}
              onSelect={() => setTab("link")}
            />

            {slug ? (
              <div className="tw-absolute tw-right-0 tw-inset-y-0 tw-p-2 tw-flex tw-items-center">
                <LikeButton
                  sourceEntity={sourceEntity}
                  subType="publications"
                  subTypeId={article.pubmedId}
                  slug={slug}
                />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {!sourceEntity?.id && !isClinical ? (
        <div className="tw-border-t tw-border-b tw-my-3 tw-p-2 tw-flex tw-gap-5 tw-items-center tw-text-xs">
          <ContentButton
            sourceEntity={sourceEntity}
            article={article}
            isActive={tab === "abstract"}
            onSelect={() => setTab("abstract")}
          />
          <LinkVariantButton
            isActive={tab === "link"}
            onSelect={() => setTab("link")}
          />
        </div>
      ) : null}

      {tab === "abstract" ? (
        <div>
          <div className="tw-text-xl tw-mt-2">Abstract</div>
          <div className="tw-h-2" />
          <div className="text-justify tw-text-sm tw-leading-relaxed">
            {article.abstract.text ? (
              <FormattedText
                highlighted$={highlighted$}
                selected$={selected$}
                domTree={article.abstract.domTree}
              />
            ) : (
              <div className="tw-text-blueGray-400">Not available</div>
            )}
          </div>
          <div className="tw-h-10" />
          {article.uniqueTags?.length ? (
            <>
              <div className="tw-px-1">
                AI Tags
                <HelperText
                  title="AI Tags"
                  fieldDescriptionKey="tag_help_text"
                />
              </div>
              <div className="tw-text-sm">
                <TagList
                  article={article}
                  highlighted$={highlighted$}
                  selected$={selected$}
                />
              </div>
            </>
          ) : null}

          <div className="tw-h-4" />
        </div>
      ) : null}
      {tab === "comments" && sourceEntity?.id ? (
        <Thread
          sourceEntity={sourceEntity}
          subType="publications"
          subTypeId={article.pubmedId}
          slug={slug}
          shouldAutoExpand={true}
          shouldHideExpandButton={true}
          shouldHideLines={true}
        />
      ) : null}

      {tab === "classifications" && sourceEntity ? (
        <>
          <Contributions
            article={article}
            pubmedId={article.pubmedId}
            showPlaceholderIfEmpty={true}
            sourceEntity={sourceEntity}
          />
        </>
      ) : null}

      {tab === "link" ? (
        <div>
          <PubmedFormWidget
            entry={
              new PubmedEntry({
                pub_med_id: article.pubmedId,
                json_data: { variants },
              })
            }
            pubmedId={article?.pubmedId}
            onCancel={() => setTab("abstract")}
            sourceEntity={sourceEntity}
          />
          <div className="tw-h-12" />
        </div>
      ) : null}
    </div>
  ) : null;
}

PubmedArticle.propTypes = {
  article: PropTypes.instanceOf(Article),
  sourceEntity: PropTypes.instanceOf(SourceEntity),
  searchEntity: PropTypes.instanceOf(SourceEntity),
  initTab: PropTypes.string,
};

PubmedArticle.defaultProps = {
  article: null,
  sourceEntity: null,
  initTab: "abstract",
  searchEntity: null,
};

function mapStateToProps(state) {
  return {
    searchEntity: getSearchEntity(state),
  };
}

export default connect(mapStateToProps)(PubmedArticle);
